import React, { useState } from 'react';
import { Card, Spinner } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import { useData } from '@microsoft/teamsfx-react';

import EnhancedExternalLink from 'src/components/atoms/enhanced-external-link/EnhancedExternalLink';
import NavigationTabs, { INavigationTabsItem } from 'src/components/molecules/navigation-tabs/NavigationTabs';
import { useStore } from 'src/hooks/useStore';
import EqualizerIcon from 'src/components/atoms/webmdhs-icon/equalizer-icon/EqualizerIcon';
import { useDailyHabitsTabs } from 'src/hooks/useDailyHabitsTabs';
import DailyHabitsTodayStatus from 'src/components/organisms/daily-habits-today-status/DailyHabitsTodayStatus';
import { useDailyHabitPlanStatus } from 'src/hooks/useDailyHabitPlanStatus';
import { DEFAULT_TODAY_TAB_VALUE, MAXIMUM_ACTIVE_PLANS, SELECT_PLANS_URL } from 'src/utils/constants';
import { getWebMdPage } from 'src/utils/utils';
import { Add24Regular } from '@fluentui/react-icons';
import WidgetContainer from '../../molecules/widget-container/WidgetContainer';
import DailyHabitsEmptyPlans from '../daily-habits-empty-plans/DailyHabitsEmptyPlans';
import DailyHabitsInterstitial from '../daily-habits-interstitial/DailyHabitsInterstitial';
import DailyHabitsInterstitialMessage from '../daily-habits-interstitial-message/DailyHabitsInterstitialMessage';
import { useDailyHabitSectionStyles } from './DailyHabitsSection.styles';

const DailyHabits: React.FC = () => {
  const styles = useDailyHabitSectionStyles();
  const [interstitialViewed, setInterstitialViewed] = useState(false);
  const { dailyHabitsStore, homeWidgetTextStore } = useStore();
  const { loading: isDailyHabitsLoading, error: dailyHabitsLoadingError } = useData(dailyHabitsStore.loadDailyHabits);
  const { loading: isHomeWidgetTextLoading, error: homeWidgetTextLoadingError } = useData(homeWidgetTextStore.loadHomeWidgetText);
  const activePlans = useDailyHabitsTabs();

  const userPlans = dailyHabitsStore.dailyHabits?.userPlans || [];

  const { hasInProgressPlans, completedPlanNames, failedPlanNames, hasCompletedOrFailedPlans } = useDailyHabitPlanStatus(userPlans, homeWidgetTextStore);

  if (dailyHabitsLoadingError || homeWidgetTextLoadingError) {
    throw dailyHabitsLoadingError || homeWidgetTextLoadingError;
  }

  if (isDailyHabitsLoading || isHomeWidgetTextLoading) {
    return <Spinner aria-label="Loading daily habits..." />;
  }

  if (!dailyHabitsStore.dailyHabits?.userPlans?.length) {
    return <DailyHabitsEmptyPlans />;
  }

  if (hasCompletedOrFailedPlans && !interstitialViewed) {
    return <DailyHabitsInterstitial completedPlanNames={completedPlanNames} failedPlanNames={failedPlanNames} onChange={() => setInterstitialViewed(true)} />;
  }

  const addPlanButtonTitle = homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Button:AddPlan');

  const getSelectPlansUrl = getWebMdPage(SELECT_PLANS_URL);
  const addPlanButton = userPlans.length < MAXIMUM_ACTIVE_PLANS
    ? (
      <div className={styles.addButton} data-testid="call-to-action">
        <EnhancedExternalLink href={getSelectPlansUrl} aria-label={addPlanButtonTitle} target="_blank" rel="noopener noreferrer">
          <Add24Regular className={styles.addButtonGap} />
          {addPlanButtonTitle}
        </EnhancedExternalLink>
      </div>
    )
    : undefined;

  const { missedActivities, todayRemainingActivities, todayTotalActivities, upcomingActivities } = dailyHabitsStore.dailyHabits;
  const todayStatusTitle = homeWidgetTextStore.getHomeWidgetText('DailyHabitsWidget:Menu:TodayStatus') || 'Today\'s Status';
  const items: INavigationTabsItem[] = [
    {
      name: todayStatusTitle,
      value: DEFAULT_TODAY_TAB_VALUE,
      icon: <span className={styles.todayStatusIcon}><EqualizerIcon /></span>,
      component: () => hasInProgressPlans
        ? (
          <DailyHabitsTodayStatus
            missedActivities={missedActivities}
            todayRemainingActivities={todayRemainingActivities}
            todayTotalActivities={todayTotalActivities}
            upcomingActivities={upcomingActivities}
          />
        )
        : <DailyHabitsInterstitialMessage completedPlanNames={completedPlanNames} failedPlanNames={failedPlanNames} />,
    },
    ...activePlans.slice(0, MAXIMUM_ACTIVE_PLANS),
  ];

  return (
    <WidgetContainer title="Daily Habits" footer={<EnhancedExternalLink as="a">View Plans</EnhancedExternalLink>}>
      <Card className={styles.container}>
        <NavigationTabs items={items} defaultTab={DEFAULT_TODAY_TAB_VALUE} callToAction={addPlanButton} />
      </Card>
    </WidgetContainer>
  );
};

export default observer(DailyHabits);
